import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError, BehaviorSubject, Subject, from, merge } from 'rxjs';
import { catchError, switchMap, filter, take } from 'rxjs/operators';
import {Router} from "@angular/router";
import {AuthService} from "../services/repositories/auth.service";
import { UserService } from '../services/repositories/user.service';

@Injectable({
  providedIn: 'root'
})
export class ErrorInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
  // Notifie les requêtes en attente lorsque le refresh échoue, pour ne pas les laisser bloquées indéfiniment.
  private refreshFailedSubject: Subject<any> = new Subject<any>();

  constructor(
    private router: Router,
    private userService: UserService,
    private _authService: AuthService
  ) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // Interception de toutes les requêtes HTTP sortantes
    return next.handle(request).pipe(

      // Gestion centralisée des erreurs HTTP
      catchError((error: HttpErrorResponse) => {
    console.log('================ ERROR INTERCEPTOR catchError ================');

        /**
         * On ne traite ici que les erreurs 401 (Unauthorized)
         * et uniquement pour les endpoints métier.
         * Les endpoints d’authentification sont exclus
         * pour éviter toute boucle infinie.
         */
        const isAuthError =
          error.status === 401 &&
          !request.url.includes('login') &&
          !request.url.includes('check-token') &&
          !request.url.includes('refresh-token');

        // Si ce n’est pas un cas de refresh token, on propage l’erreur
        if (!isAuthError) {
          return throwError(() => error);
        }

        /**
         * Si un refresh de token est déjà en cours :
         * - on ne relance PAS un nouveau refresh
         * - on met la requête en attente
         * - elle sera rejouée une fois le token rafraîchi
         */
        if (this.isRefreshing) {
          return this.handleRequestQueue(request, next);
        }

        /**
         * Début du processus de rafraîchissement du token :
         * - on verrouille le refresh
         * - on vide le subject pour bloquer les requêtes suivantes
         */
        this.isRefreshing = true;
        this.refreshTokenSubject.next(null);

        /**
         * Appel backend pour obtenir un nouveau token
         * à partir du refresh token
         */
        return this._authService.refreshAuthToken().pipe(

          /**
           * Une fois les nouveaux tokens reçus :
           * - on les sauvegarde dans le Secure Storage
           * - on attend la fin de cette sauvegarde (Promise → Observable)
           * - on rejoue la requête originale avec le nouveau token
           */
          switchMap((resRefreshToken: any) =>
            from(
              this._authService.saveTokens(
                resRefreshToken.authToken,
                resRefreshToken.refreshToken
              )
            ).pipe(
              switchMap(() => {

                // Fin du refresh : on déverrouille
                this.isRefreshing = false;

                // On notifie les requêtes en attente avec le token (string), pas l'objet AuthToken.
                this.refreshTokenSubject.next(resRefreshToken.authToken.valeur);

                // Rejeu de la requête initiale avec le token rafraîchi
                return next.handle(
                  this.addToken(
                    request,
                    resRefreshToken.authToken.valeur
                  )
                );
              })
            )
          ),

          /**
           * Si le refresh échoue :
           * - on considère la session comme invalide
           * - on redirige vers la page de login
           * - on propage l’erreur
           */
          catchError((err) => {
            this.isRefreshing = false;
            this.userService.resetCurrentUser();
            if(this.router.url !== '/plan-de-salle') {
              this.router.navigate(['login']);
            }
            // On débloque les requêtes en attente : sans ça, elles ne recevraient
            // jamais ni succès ni erreur et resteraient pendantes indéfiniment.
            this.refreshFailedSubject.next(err);
            return throwError(() => err);
          })
        );
      })
    );
  }


  private handleRequestQueue(request: HttpRequest<any>, next: HttpHandler) {
    return merge(
      this.refreshTokenSubject.pipe(
        filter((token): token is string => token !== null),
        take(1),
        switchMap((token) => next.handle(this.addToken(request, token)))
      ),
      this.refreshFailedSubject.pipe(
        take(1),
        switchMap((err) => throwError(() => err))
      )
    ).pipe(take(1));
  }

  private addToken(request: HttpRequest<any>, token: string) {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`
      }
    });
  }
}
